import React from 'react'

import styles from '../../styles'
import {
  validateYearlyRangeEndTime,
  validateYearlyRangeStartTime,
  validateYearlyRangeTimesOverlaps,
} from '../../utils'
import AddIcon from '../../parts/icons/add'
import RemoveIcon from '../../parts/icons/remove'
import SquareCheckBox from '../../../../../../../../Assets/Images/icons/squareCheckBox'
import DayAndMonthSelector from '../../../../../../../../Components/_newInputs/DayAndMonthSelector'
import TooltipWrapper from '../../../../../../../../Components/_newComponents/TooltipWrapper'

const YearlyScheduling = ({
  yearlySchedule,
  setYearlySchedule,
  visible,
  validationErrors,
  handleValidationError,
  includeYearlySchedule,
  setIncludeYearlySchedule,
}) => {
  if (!visible) return null

  const onTimeRangesStartChange = (id, time, end) => {
    setYearlySchedule(prev =>
      prev.map(timeRange =>
        timeRange.id === id ? { ...timeRange, start: time } : timeRange,
      ),
    )
    const newYearlySchedule = yearlySchedule.map(timeRange =>
      timeRange.id === id ? { ...timeRange, start: time } : timeRange,
    )

    let hasErrors = false
    hasErrors = validateYearlyRangeStartTime(
      id,
      time,
      end,
      handleValidationError,
    )
    const endTimeError = validationErrors[`yearly-time-range-end-${id}`]

    if (endTimeError) {
      hasErrors = validateYearlyRangeEndTime(
        id,
        time,
        end,
        handleValidationError,
      )
    }
    if (hasErrors) {
      return
    }
    validateYearlyRangeTimesOverlaps(newYearlySchedule, handleValidationError)
  }

  const onTimeRangesEndChange = (id, time, start) => {
    setYearlySchedule(prev =>
      prev.map(timeRange =>
        timeRange.id === id ? { ...timeRange, end: time } : timeRange,
      ),
    )
    const newYearlySchedule = yearlySchedule.map(timeRange =>
      timeRange.id === id ? { ...timeRange, end: time } : timeRange,
    )

    let hasErrors = false
    hasErrors = validateYearlyRangeEndTime(
      id,
      start,
      time,
      handleValidationError,
    )
    const startTimeError = validationErrors[`yearly-time-range-start-${id}`]
    if (startTimeError) {
      hasErrors = validateYearlyRangeStartTime(
        id,
        start,
        time,
        handleValidationError,
      )
    }
    if (hasErrors) {
      return
    }
    validateYearlyRangeTimesOverlaps(newYearlySchedule, handleValidationError)
  }

  const handleAddTimeRange = () => {
    const newTimeRanges = [
      ...yearlySchedule,
      {
        id: Math.max(...yearlySchedule.map(timeRange => timeRange.id), 0) + 1,
        start: null,
        end: null,
      },
    ]
    setYearlySchedule(newTimeRanges)
  }

  const handleRemoveTimeRange = id => {
    const newTimeRanges = yearlySchedule.filter(
      timeRange => timeRange.id !== id,
    )
    setYearlySchedule(newTimeRanges)
    handleValidationError(`yearly-time-range-start-${id}`, null)
    handleValidationError(`yearly-time-range-end-${id}`, null)
    validateYearlyRangeTimesOverlaps(newTimeRanges, handleValidationError)
  }

  const handleIncludeYearlySchedule = () => {
    if (includeYearlySchedule) {
      Array.from({ length: 4 }).forEach((_, index) => {
        handleValidationError(`yearly-time-range-start-${index + 1}`, null)
        handleValidationError(`yearly-time-range-end-${index + 1}`, null)
      })
      return setIncludeYearlySchedule(!includeYearlySchedule)
    } else {
      return setIncludeYearlySchedule(!includeYearlySchedule)
    }
  }

  return (
    <div style={pageStyles.contentArea}>
      <div style={pageStyles.yearlyScheduleHeader}>
        <div onClick={handleIncludeYearlySchedule}>
          <SquareCheckBox active={includeYearlySchedule} />
        </div>
        <div style={styles.headerSchedulingContainer}>
          <TooltipWrapper description="Define recurring yearly date ranges (e.g., for a seasonal location). These schedules will repeat every year.">
            <div style={{ ...styles.contentTitle, cursor: 'pointer' }}>
              Yearly Schedule ⓘ
            </div>
          </TooltipWrapper>
          <div style={{ ...pageStyles.subtitle, alignSelf: 'flex-start' }}>
            Define the dates this location is operational in a year.
          </div>
        </div>
      </div>
      {includeYearlySchedule && (
        <div>
          {yearlySchedule.map(timeRange => (
            <div key={timeRange.id} style={pageStyles.inputsContainer}>
              <div style={pageStyles.yearlyScheduleRow}>
                <DayAndMonthSelector
                  value={timeRange.start}
                  onChange={time =>
                    onTimeRangesStartChange(timeRange.id, time, timeRange.end)
                  }
                  validationErrors={validationErrors}
                  validationKey={`yearly-time-range-start-${timeRange.id}`}
                  label="Starting Date"
                />
                <DayAndMonthSelector
                  value={timeRange.end}
                  onChange={time =>
                    onTimeRangesEndChange(timeRange.id, time, timeRange.start)
                  }
                  validationErrors={validationErrors}
                  validationKey={`yearly-time-range-end-${timeRange.id}`}
                  label="Ending Date"
                />
              </div>
              {yearlySchedule.length !== 1 && (
                <button
                  style={pageStyles.removeButton}
                  onClick={() => handleRemoveTimeRange(timeRange.id)}
                >
                  <RemoveIcon />
                </button>
              )}
            </div>
          ))}
          {yearlySchedule.length < 4 && (
            <div style={pageStyles.addButtonContainer}>
              <button style={pageStyles.addButton} onClick={handleAddTimeRange}>
                <AddIcon />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default YearlyScheduling

const pageStyles = {
  subtitle: {
    color: '#707087',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
  },
  inputsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 10,
  },
  datePickerInput: error => ({
    backgroundColor: 'white',
    width: '100%',
    height: 32,
    paddingLeft: 16,
    paddingRight: 40,
    borderRadius: 8,
    border: '1px solid #E2E2FC',
    borderColor: error ? '#FF0000' : '#E2E2FC',
  }),
  datePickerContainer: {
    width: '44%',
    gap: 6,
    display: 'flex',
    flexDirection: 'column',
  },
  errorMessage: {
    color: 'red',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '18px',
  },
  contentArea: {
    ...styles.contentArea,
    marginTop: 14,
  },
  removeButton: {
    width: 'fit-content',
    background: 'transparent',
    border: 'none',
    padding: 0,
    alignSelf: 'flex-end',
    marginBottom: 6,
  },
  addButton: {
    width: 'fit-content',
    background: 'transparent',
    border: 'none',
    padding: 0,
  },
  yearlyScheduleRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    width: '94%',
  },
  addButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  yearlyScheduleHeader: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
  },
}
